import {
  useAssetStatusList,
  useAssetType,
  useFacilitiesList,
  useSystemOptions,
} from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useMemo } from "react";

export const AssetSearchFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: any) => {
  const { data: assetStatusList } = useAssetStatusList();
  const { data: facilityList } = useFacilitiesList();

  const assetStatusOptions = useMemo(() => {
    return (
      assetStatusList?.result?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || []
    );
  }, [assetStatusList]);

  const facilityOptions = useMemo(() => {
    return (
      facilityList?.map((facility: any) => ({
        value: facility.id,
        label: facility.facilityName,
      })) || []
    );
  }, [facilityList]);

  const { data: assetTypeList } = useAssetType();

  const { data: systemList } = useSystemOptions();

  const assetTypeOptions = useMemo(() => {
    return (
      assetTypeList?.result?.map((assetType: any) => ({
        value: assetType.code,
        label: assetType.name,
      })) || []
    );
  }, [assetTypeList]);

  const systemOptions = useMemo(() => {
    return (
      systemList?.map((system: any) => ({
        value: system.id,
        label: system.systemNo,
      })) || []
    );
  }, [systemList]);

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "asset_type_code",
        label: "Asset Type",
        type: "select",
        required: false,
        options: [{ label: "All Types", value: "all" }, ...assetTypeOptions],
        classes: FIELD_CLASSES,
        defaultValue: filters.type || "all",
      },
      {
        name: "facility",
        label: "Facility",
        type: "select",
        required: false,
        options: [
          { label: "All Facilities", value: "all" },
          ...facilityOptions,
        ],
        classes: FIELD_CLASSES,
        defaultValue: filters.facility || "all",
      },
      {
        name: "system",
        label: "System",
        type: "select",
        required: false,
        options: [{ label: "All Systems", value: "all" }, ...systemOptions],
        classes: FIELD_CLASSES,
        defaultValue: filters.system || "all",
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        required: false,
        placeholder: "All Statuses",
        options: [
          { label: "All Statuses", value: "all" },
          ...assetStatusOptions,
        ],
        classes: FIELD_CLASSES,
        defaultValue: filters.status || "all",
      },
    ],
    [assetStatusOptions, facilityOptions, filters]
  );

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: filters,
  });
  const handleSubmit = (data: any) => {
    onApplyFilters({ ...data, page: 1 });
  };

  const handleReset = () => {
    const defaultFilters: any = {
      type: "all",
      facility: "all",
      system: "all",
      network: "all",
      location: "",
      status: "all",
      page: 1,
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-2 mt-4">
      <Button onClick={handleReset} variant="outline">
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        Clear Filters
      </Button>

      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleSubmit}
        gridColumns={4}
        responsiveColumns={{
          sm: 1,
          md: 2,
          lg: 4,
        }}
        config={{
          layout: "grid",
          showSubmitButton: false,
          showResetButton: false,
          spacing: "medium",
        }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
